import React, { useRef, memo, useMemo, useCallback, useState } from "react";
import { View, TouchableOpacity, StyleSheet } from "react-native";
import { useSortable } from "@dnd-kit/sortable";
import {
  Feather,
  MaterialCommunityIcons,
  MaterialIcons,
} from "@expo/vector-icons";
import Tooltip from "../../Components/Tooltip";
import Colors from "../../../Resources/Color";
import { BlockPlanificationConstants } from "../../../Resources/Constants";
import { useDevice } from "../../_layout";
import { useTranslation } from "react-i18next";

// Enable drag-and-drop for set reordering
const ENABLE_SET_DRAGGING = true;

// Styles for the draggable component (defined outside to avoid recreation)
const draggableStyles = StyleSheet.create({
  setRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: Colors.lightGrayColor,
    borderRadius: 10,
    width: "100%",
    paddingHorizontal: 0,
    outlineStyle: "none",
    outlineWidth: 0,
    borderWidth: 0,
  },
  setRowDraggable: {
    // Smooth transitions for opacity, scale, and transform
    // Transform transition allows smooth repositioning when other items move
    transitionProperty: "opacity, transform",
    transitionDuration: "200ms",
    transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
  },
  setRowViewMode: {},
  setRowEditMode: {
    backgroundColor: "transparent",
  },
  setRowDragging: {
    opacity: 0.95,
    pointerEvents: "none",
    zIndex: 1000,
    backgroundColor: "transparent",
    transform: [{ scale: 1.02 }],
    // Smooth scale and opacity transition when drag starts/ends
    transitionProperty: "opacity, transform",
    transitionDuration: "150ms",
    transitionTimingFunction: "ease-out",
    // Performance optimization for smooth dragging
    willChange: "transform",
  },
  setRowContentWrapper: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  setRowActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginLeft: 8,
  },
  setCopyBtn: {
    width: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  setDeleteBtn: {
    width: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  setDragHandle: {
    width: 18,
    alignItems: "center",
    justifyContent: "center",
    cursor: "grab",
    touchAction: "manipulation",
    WebkitTouchCallout: "none",
    userSelect: "none",
    WebkitUserSelect: "none",
  },
});

// Lightweight set row for view mode - no drag/drop overhead
export const ViewModeSetRow = memo(({ children }) => {
  const { isMobile } = useDevice();
  const pillHeight = isMobile
    ? BlockPlanificationConstants.MOBILE_VALUE_PILL_HEIGHT
    : BlockPlanificationConstants.VALUE_PILL_HEIGHT;

  return (
    <View
      style={[
        draggableStyles.setRow,
        draggableStyles.setRowDraggable,
        draggableStyles.setRowViewMode,
        isMobile && { gap: 6 },
      ]}
    >
      {children}
    </View>
  );
});

// Memoized draggable row for edit mode - includes drag/drop logic
export const EditModeSetRow = memo(
  ({
    setItem,
    idx,
    children,
    onMoveSet,
    onDuplicateSerie,
    onDeleteSerie,
    moveCursorStyle,
  }) => {
    const { isMobile } = useDevice();
    const { t } = useTranslation();
    const pillHeight = isMobile
      ? BlockPlanificationConstants.MOBILE_VALUE_PILL_HEIGHT
      : BlockPlanificationConstants.VALUE_PILL_HEIGHT;

    const MOBILE_ICON_WIDTH = 20;
    const iconSize = isMobile ? 16 : 16;

    const containerRef = useRef(null);
    const [isCopyHovered, setIsCopyHovered] = useState(false);
    const [isDeleteHovered, setIsDeleteHovered] = useState(false);
    const [isDragHovered, setIsDragHovered] = useState(false);

    // Use @dnd-kit for all platforms
    const { attributes, listeners, setNodeRef, transform, isDragging } =
      useSortable({
        id: setItem?.id,
        disabled: setItem?.id === "placeholder" || !ENABLE_SET_DRAGGING,
      });

    // Create merged ref callback for container
    const mergedContainerRef = useMemo(() => {
      return (node) => {
        containerRef.current = node;
        if (setNodeRef) {
          setNodeRef(node);
        }
      };
    }, [setNodeRef]);

    // Convert dnd-kit transform to React Native style format
    // When dragging: no transition (instant, smooth drag)
    // When not dragging: transition enabled (smooth repositioning of other items)
    const transformStyle = useMemo(() => {
      if (!transform) {
        return null;
      }
      const { x, y } = transform;
      return {
        transform: [{ translateX: x }, { translateY: y }],
        // Disable transition only during active drag for instant feedback
        // When drag ends and transform resets, transition will animate smoothly
        transition: isDragging ? "none" : undefined,
      };
    }, [transform, isDragging]);

    // Handler to dismiss keyboard before drag starts
    const handleDragHandlePress = useCallback(() => {
      if (typeof document !== "undefined") {
        const activeElement = document.activeElement;
        // Blur any focused input/textarea to dismiss keyboard
        if (
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            activeElement.contentEditable === "true")
        ) {
          activeElement.blur();
        }
      }
    }, []);

    // Merge drag listeners with keyboard dismissal
    // When dragging is disabled, listeners will be empty, so mergedDragListeners will be empty too
    const mergedDragListeners = useMemo(() => {
      if (
        !ENABLE_SET_DRAGGING ||
        !listeners ||
        Object.keys(listeners).length === 0
      ) {
        return {};
      }
      return {
        ...listeners,
        onTouchStart: (e) => {
          // Dismiss keyboard on touch devices
          handleDragHandlePress();
          if (listeners?.onTouchStart) {
            listeners.onTouchStart(e);
          }
        },
        onPointerDown: (e) => {
          // Dismiss keyboard on pointer devices
          handleDragHandlePress();
          if (listeners?.onPointerDown) {
            listeners.onPointerDown(e);
          }
        },
      };
    }, [listeners, handleDragHandlePress]);

    return (
      <View
        ref={mergedContainerRef}
        style={[
          draggableStyles.setRow,
          draggableStyles.setRowDraggable,
          draggableStyles.setRowEditMode,
          isDragging && draggableStyles.setRowDragging,
          transformStyle,
          isMobile && { gap: 6 },
        ]}
        {...(ENABLE_SET_DRAGGING ? attributes : {})}
      >
        <View
          style={[
            draggableStyles.setRowContentWrapper,
            isDragging && draggableStyles.setRowContentDragging,
            isMobile && { gap: 6 },
          ]}
        >
          {children}

          <View
            style={[
              draggableStyles.setRowActions,
              isMobile && { gap: 8, marginLeft: 4 },
            ]}
          >
            <Tooltip
              content={t("copyToNextSet-string")}
              placement="top"
              delayedOpen={true}
            >
              <TouchableOpacity
                style={[
                  draggableStyles.setCopyBtn,
                  { height: pillHeight },
                  isMobile && { width: MOBILE_ICON_WIDTH },
                ]}
                onPress={() => {
                  if (setItem?.id === "placeholder") return;
                  onDuplicateSerie?.(setItem);
                }}
                onMouseEnter={() => !isMobile && setIsCopyHovered(true)}
                onMouseLeave={() => !isMobile && setIsCopyHovered(false)}
              >
                <MaterialCommunityIcons
                  name="arrow-down-right"
                  size={iconSize}
                  color={
                    isCopyHovered ? Colors.darkBubbleColor : Colors.bubbleColor
                  }
                />
              </TouchableOpacity>
            </Tooltip>

            <Tooltip
              content={t("deleteSet-string")}
              placement="top"
              delayedOpen={true}
            >
              <TouchableOpacity
                style={[
                  draggableStyles.setDeleteBtn,
                  { height: pillHeight },
                  isMobile && { width: MOBILE_ICON_WIDTH },
                ]}
                onPress={() => {
                  if (setItem?.id === "placeholder") return;
                  onDeleteSerie?.(setItem);
                }}
                onMouseEnter={() => !isMobile && setIsDeleteHovered(true)}
                onMouseLeave={() => !isMobile && setIsDeleteHovered(false)}
              >
                <Feather
                  name="x"
                  size={iconSize}
                  color={
                    isDeleteHovered
                      ? Colors.darkBubbleColor
                      : Colors.bubbleColor
                  }
                />
              </TouchableOpacity>
            </Tooltip>

            {ENABLE_SET_DRAGGING && (
              <View
                style={[
                  draggableStyles.setDragHandle,
                  { height: pillHeight },
                  isMobile && { width: MOBILE_ICON_WIDTH },
                  moveCursorStyle,
                ]}
                {...mergedDragListeners}
                onMouseEnter={() => !isMobile && setIsDragHovered(true)}
                onMouseLeave={() => !isMobile && setIsDragHovered(false)}
              >
                <MaterialIcons
                  name="drag-indicator"
                  size={iconSize}
                  color={
                    isDragHovered ? Colors.darkBubbleColor : Colors.bubbleColor
                  }
                />
              </View>
            )}
          </View>
        </View>
      </View>
    );
  },
  (prevProps, nextProps) => {
    // Return true to SKIP re-render (props are equal)
    if (prevProps.setItem?.id !== nextProps.setItem?.id) return false;
    if (prevProps.idx !== nextProps.idx) return false;
    return true;
  },
);
